// Montant minimum de la liste des projets, en millions d'euros (estimation au dernier cours, colonne `budget_eur` de
// `projects_summary`). Les fiches sans montant connu restent visibles par défaut : la presse et les bailleurs n'en
// donnent souvent pas, et les cacher ferait perdre des projets réels.

const STRICT = "budget_eur@gte";
const OR = "@or";

export type AmountFilter = { millions: number; includeUnknown: boolean };

const orForm = (value: unknown): number | null => {
  if (!value || typeof value !== "object") return null;
  const entries = value as Record<string, unknown>;
  const keys = Object.keys(entries).sort();
  if (keys.join() !== "budget_eur@gte,budget_eur@is") return null;
  if (entries["budget_eur@is"] !== "null") return null;
  const min = Number(entries[STRICT]);
  return Number.isFinite(min) ? min : null;
};

export const readAmountFilter = (
  filterValues: Record<string, unknown>,
): AmountFilter | null => {
  const withUnknown = orForm(filterValues[OR]);
  if (withUnknown != null)
    return { millions: withUnknown / 1_000_000, includeUnknown: true };
  const strict = Number(filterValues[STRICT]);
  if (filterValues[STRICT] != null && Number.isFinite(strict))
    return { millions: strict / 1_000_000, includeUnknown: false };
  return null;
};

/** Filtres de la liste avec ce montant minimum, à la place du précédent ; sans montant, le filtre est retiré. */
export const applyAmountFilter = (
  filterValues: Record<string, unknown>,
  millions: number | null,
  includeUnknown: boolean,
): Record<string, unknown> => {
  const rest = Object.fromEntries(
    Object.entries(filterValues).filter(
      ([key, value]) =>
        key !== STRICT && !(key === OR && orForm(value) != null),
    ),
  );
  if (millions == null || !(millions > 0)) return rest;
  const min = Math.round(millions * 1_000_000);
  return includeUnknown
    ? { ...rest, [OR]: { [STRICT]: min, "budget_eur@is": "null" } }
    : { ...rest, [STRICT]: min };
};
